import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { inject } from '@angular/core';
import {
  RectilinearView,
  Scene,
  Viewer,
  ViewerOptions,
  WebGlStage
} from 'marzipano';
import { LoggingService } from '@newroom-connect/library/services';

import { IMarzipanoScene, IMarzipanoViewState, IMarzipanoSceneConfig, IMarzipanoFovLimits, IMarzipanoViewParams } from '../interfaces/marzipano-scene.interface';
import { SceneState, IEvent, SceneEvent } from '../interfaces/scene.interface';
import { MarzipanoCoordinateService } from '../services/marzipano-coordinate.service';
import { MarzipanoLayerService } from '../services/marzipano-layer.service';

import { CubemapFaces, IMarzipanoLoadingProgress, MarzipanoCubemapLoader } from './marzipano-cubemap-loader.model';
import { MarzipanoSceneHotspot } from './marzipano-scene-hotspot.model';
import { MarzipanoView } from './marzipano-view.model';

/**
 * Clean, focused Marzipano-based 360° scene implementation.
 *
 * This refactored version:
 * - Properly delegates to MarzipanoCubemapLoader for loading
 * - Delegates hotspot management to MarzipanoSceneHotspot
 * - Focuses solely on scene lifecycle and view management
 * - Uses simplified, Marzipano-specific interfaces.
 */
export class MarzipanoScene implements IMarzipanoScene {
  private readonly logger = inject(LoggingService);

  public readonly coordinateService = inject(MarzipanoCoordinateService);

  public sceneView: RectilinearView | null = null;

  // Base scene properties
  public isInitialized = false;
  public container: HTMLElement | null = null;

  public state$ = new BehaviorSubject<SceneState>(SceneState.IDLE);
  public events$ = new BehaviorSubject<IEvent<unknown>>({
    type: SceneEvent.LOADING_PROGRESS,
    data: undefined,
    timestamp: Date.now()
  });

  // Marzipano instances - public for direct access
  public marzipanoScene: Scene | null = null;
  public marzipanoViewer: Viewer | null = null;

  // Dedicated managers - public for direct access
  public cubemapLoader = new MarzipanoCubemapLoader();
  public hotspotManager = new MarzipanoSceneHotspot();
  public viewManager = new MarzipanoView();
  public layerManager = inject(MarzipanoLayerService);

  /**
   * Initialize the Marzipano scene with a container element.
   *
   * @param container - The HTML container element.
   * @param config - Optional Marzipano scene configuration.
   * @returns Promise that resolves when initialization is complete.
   */
  public async initialize(container: HTMLElement, config?: IMarzipanoSceneConfig): Promise<void> {
    try {
      if (this.isInitialized) {
        this.logger.warn('Scene already initialized');
        return;
      }

      this.logger.info('Initializing Marzipano scene', { config });
      this.state$.next(SceneState.INITIALIZING);

      this.container = container;

      // Create Marzipano viewer
      await this.createMarzipanoViewer(config);

      // Create initial view
      this.createInitialView(config);

      this.isInitialized = true;
      this.state$.next(SceneState.READY);
      this.emitEvent(SceneEvent.READY);

      this.logger.info('Marzipano scene initialized successfully');
    } catch (error) {
      this.logger.error('Failed to initialize Marzipano scene', error as Error);
      this.state$.next(SceneState.ERROR);
      this.emitEvent(SceneEvent.ERROR, error);
      throw error;
    }
  }

  /**
   * Load cubemap using 6-face URLs.
   * Delegates to the dedicated MarzipanoCubemapLoader.
   *
   * @param faces - The cubemap faces to load.
   * @returns Promise that resolves when loading is complete.
   */
  public async loadCubemap(faces: CubemapFaces): Promise<void> {
    try {
      if (!this.marzipanoViewer) {
        throw new Error('Scene not initialized. Call initialize() first.');
      }

      this.logger.info('Loading cubemap with dedicated loader', { faces });
      this.state$.next(SceneState.LOADING);

      // Set up progress tracking from the loader
      this.cubemapLoader.watchLoadingProgress$().subscribe(progress => {
        this.emitEvent(SceneEvent.LOADING_PROGRESS, progress);
      });

      // Use the dedicated cubemap loader with optimized performance settings
      this.marzipanoScene = await this.cubemapLoader.loadCubemap(
        this.marzipanoViewer,
        faces,
        {
          crossOrigin: 'use-credentials',
          withCredentials: true,
          initialView: this.sceneView?.parameters() || { yaw: 0, pitch: 0, fov: Math.PI / 3 },
          maxResolution: MarzipanoCubemapLoader.DEFAULT_VIEWER_LIMIT.maxResolution,            // Increased for better quality with progressive loading
          maxVFov: MarzipanoCubemapLoader.DEFAULT_VIEWER_LIMIT.maxVFov,   // Optimized vertical FOV
          maxHFov: MarzipanoCubemapLoader.DEFAULT_VIEWER_LIMIT.maxHFov    // Wider horizontal FOV for better coverage
        }
      );

      if (!this.marzipanoScene) {
        throw new Error('Failed to load cubemap');
      }

      // Initialize hotspot manager with the scene's hotspot container
      const hotspotContainer = this.marzipanoScene.hotspotContainer();

      this.hotspotManager.initialize(hotspotContainer);

      // Initialize view manager with the scene and view
      if (this.sceneView) {
        this.viewManager.initialize(this.marzipanoScene, this.marzipanoScene.view() as RectilinearView);
      }

      // Initialize layer manager with the viewer's stage
      if (this.marzipanoViewer) {
        const stage = this.marzipanoViewer.stage();

        this.layerManager.initialize(stage);
        this.logger.debug('Layer manager initialized with stage');
      }

      // Initialize coordinate service with the scene's connected view (not the initial view)
      if (this.marzipanoScene && this.container) {
        // Use the scene's view which is properly connected to the stage and has dimensions
        const sceneView = this.marzipanoScene.view();

        this.coordinateService.initialize(sceneView as RectilinearView, this.container);
        this.logger.debug('Coordinate service initialized with scene view', sceneView, {
          viewWidth: sceneView.width(),
          viewHeight: sceneView.height()
        });
      }

      this.state$.next(SceneState.READY);
      this.emitEvent(SceneEvent.READY, this.marzipanoScene);

      this.logger.info('Cubemap loaded successfully');
    } catch (error) {
      this.logger.error('Failed to load cubemap', error as Error);
      this.state$.next(SceneState.ERROR);
      this.emitEvent(SceneEvent.ERROR, error);
      throw error;
    }
  }

  /**
   * Set the view parameters (yaw, pitch, fov).
   *
   * @param viewState - The view state to apply.
   */
  public setViewState(viewState: Partial<IMarzipanoViewState>): void {
    try {
      this.viewManager.setViewState(viewState, (newViewState) => {
        this.emitEvent(SceneEvent.VIEW_CHANGE, newViewState);
      });
    } catch (error) {
      this.logger.error('Failed to set view state', error as Error);
      this.emitEvent(SceneEvent.ERROR, error);
    }
  }

  /**
   * Animate to a specific view state.
   *
   * @param viewState - The target view state to animate to.
   * @param duration - Animation duration in milliseconds (default: 1000).
   * @returns Promise that resolves when animation completes.
   */
  public async animateToViewState(viewState: Partial<IMarzipanoViewState>, duration = 1000): Promise<void> {
    return this.viewManager.animateToViewState(viewState, (newViewState) => {
      this.emitEvent(SceneEvent.VIEW_CHANGE, newViewState);
    }, duration);
  }

  /**
   * Get current camera state with spherical coordinates.
   * Returns the current camera position and orientation in the 3D scene.
   *
   * @returns Current camera state with spherical coordinates (yaw, pitch, fov, roll).
   */
  public getCurrentCameraState(): IMarzipanoViewState {
    return this.viewManager.getCurrentCameraState();
  }

  /**
   * Calculate default radius based on viewport and field of view.
   * This provides a reasonable default distance for hotspots.
   *
   * @returns The calculated radius value in pixels.
   */
  public calculateDefaultRadius(): number {
    try {
      if (!this.marzipanoScene) {
        return 1000; // Fallback radius
      }

      const view = this.marzipanoScene.viewer().view();
      const stage = this.marzipanoScene.viewer().stage();

      if (!view || !stage) {
        return 1000; // Fallback radius
      }

      const fov = (view as RectilinearView).fov?.();
      const viewHeight = stage.height();

      if (fov && viewHeight) {
        // Calculate radius based on viewport height and field of view
        const radius = (viewHeight / 2) / Math.tan(fov / 2);

        return Math.max(radius, 500); // Minimum radius of 500
      }

      return 1000; // Fallback radius
    } catch (error) {
      this.logger.warn('Error calculating default radius, using fallback', error as Error);
      return 1000; // Fallback radius
    }
  }

  // Hotspot management - use scene.hotspotManager directly for all hotspot operations

  // Required interface methods
  public get loadingProgress$(): Observable<IMarzipanoLoadingProgress> {
    return this.cubemapLoader.watchLoadingProgress$();
  }

  /**
   * Subscribe to specific scene events.
   * Returns an observable stream of events filtered by the specified event type.
   *
   * @param eventType - The type of scene event to listen for (e.g., SceneEvent.READY, SceneEvent.ERROR).
   *
   * @returns Observable stream of events with the specified type, including event data and timestamp.
   */
  public on<T = unknown>(eventType: SceneEvent): Observable<IEvent<T>> {
    return this.events$.pipe(
      filter(e => e.type === eventType)
    ) as Observable<IEvent<T>>;
  }

  /**
   * Dispose of the scene and clean up resources.
   */
  public dispose(): void {
    try {
      this.logger.info('Disposing Marzipano scene');

      // Dispose managers
      this.hotspotManager.dispose();
      this.viewManager.dispose();
      this.layerManager.dispose();

      // Dispose cubemap loader
      this.cubemapLoader.disposeProgressSubject();

      // Destroy Marzipano scene
      if (this.marzipanoScene) {
        this.marzipanoScene.destroy();
        this.marzipanoScene = null;
      }

      // Destroy Marzipano viewer
      if (this.marzipanoViewer) {
        this.marzipanoViewer.destroy();
        this.marzipanoViewer = null;
      }

      this.sceneView = null;
      this.isInitialized = false;

      this.state$.next(SceneState.DISPOSED);
      this.emitEvent(SceneEvent.DISPOSED);

      this.logger.info('Marzipano scene disposed');
    } catch (error) {
      this.logger.error('Error during scene disposal', error as Error);
      this.emitEvent(SceneEvent.ERROR, error);
    }
  }

  /**
   * Create Marzipano viewer with configuration.
   *
   * @param config - Marzipano scene configuration.
   */
  private async createMarzipanoViewer(config?: IMarzipanoSceneConfig): Promise<void> {
    const viewerOptions: ViewerOptions = {
      controls: {
        mouseViewMode: config?.controls?.mouseViewMode || 'drag'
      },
      stage: new WebGlStage(config?.viewerOptions?.stage ?? {})
    };

    if (!this.container) {
      throw new Error('Container is required for viewer creation');
    }

    this.marzipanoViewer = new Viewer(this.container, viewerOptions);

    this.logger.debug('Marzipano viewer created', viewerOptions);
  }

  /**
   * Create initial view for Marzipano.
   *
   * @param config - Marzipano scene configuration.
   */
  private createInitialView(config?: IMarzipanoSceneConfig): void {
    const initialParams: IMarzipanoViewParams = {
      yaw: 0,
      pitch: 0,
      fov: Math.PI / 3 // 60 degrees
    };

    const limits: IMarzipanoFovLimits = {
      maxResolution: config?.quality?.maxResolution || 512,
      maxVFov: 90 * Math.PI / 180,
      maxHFov: 100 * Math.PI / 180
    };

    const limiter = RectilinearView.limit.traditional(
      limits.maxResolution,
      limits.maxVFov,
      limits.maxHFov
    );

    this.sceneView = new RectilinearView(initialParams, limiter);
    this.logger.debug('Marzipano view created', initialParams);
  }

  /**
   * Emit a scene event with optional data.
   * Creates and broadcasts an event through the events$ observable stream.
   *
   * @param type - The type of scene event to emit.
   * @param data - Optional data to include with the event.
   */
  private emitEvent(type: SceneEvent, data?: unknown): void {
    this.events$.next({ type, data, timestamp: Date.now() });
  }
}
